/**
 * Every read the Cockpit makes, in one place — so the four figures, the decisions, the three app
 * tiles and the coverage strip are computed from the SAME query states.
 *
 * **DEC-87 (package A, 2026-09-30): the Cockpit is synthesis only.** The reads that fed only the
 * sections that moved to their app's pages — the size-run repair outcome, the post-season season
 * and lost-sales reads, the launch directory and the exit impact reading — are no longer made
 * here; each is made by the page that now draws it. What stays feeds the KPIs, "Needs a
 * decision", the app tiles, and the price-run resolution (F16).
 *
 * **The feeds are read again (package C7, brief part 1 §6).** The designer's «Copertura dati» is
 * a table of FEEDS — Feed · Ultimo invio · Stato — and the only read that answers it is
 * `GET /foundations/feeds`, the Data ▸ Feeds page's own. It is read for the market of the run on
 * screen, never a market chosen here: with no run it is not asked (DEC-85).
 *
 * Each call is the chapter page's own operation with the chapter page's own parameters, so a
 * planner who follows a row or a tile lands on a warm cache entry and the two cannot disagree
 * about a count: the price actions are `PriceDecisionQueue`'s `{ run_id }`, the moves are the live
 * move-run directory the Compacting pages read (`useMoveRuns`, bata-reqs ADR 9002 stage 3), D2 evidence is the chosen
 * date's or each market's newest, and the decision summary is bata-platform#406's (ADR 6030). The
 * in-season impact reading is the strategic chapter's hook, read to tell a price run from the
 * cycle's other runs (F16). `AppTiles` and `NeedsDecision` render from what this returns rather
 * than asking again.
 *
 * **What is NOT walked any more (audit 3 and 4, performance).** The page never idled in 45 s on CO
 * because it paged collections only to add them up: the run's proposals (32 requests of 1000), the
 * position run's option roll-up (16 of 500) and the open alerts (7 of 1000). Each figure is now one
 * read from the store: the proposal book is the run digest's (`GET /inseason/runs/{id}/digest`,
 * ADR 6039 — the findings charts' own cache entry), the position run is the residual distribution
 * (the exit estate page's own entry) with the position run's digest for its doors, and the open
 * alerts are the alert groups' `totals.alerts`. Audit 4 moved the last two walks: the price steps
 * (steps, articles, weeks, first week, recovery) are the run digest's `price_steps` book with the
 * decision summary's pending figures (ADR 6173). The consolidation lanes are no longer read: the
 * legacy writer writes none since platform v0.126.0 (owner ruling 2026-11-02). Which run holds the price steps is read off the
 * run directory's own rows (`price_actions`, ADR 6176; `resolvePriceRun`), not asked of ten price-step
 * walks nor of the decision summaries one by one. Nothing on this page is walked page by page any more.
 *
 * **What the coverage covers, and why not more.** The chapter manifests declare no reads (their
 * contract has routes, exports and drills — `src/features/manifest.ts`), so there is no estate-wide
 * list this page could honestly iterate. The strip therefore covers exactly the reads THIS page
 * makes, and says so on screen. A run-bound read whose run is not known yet is not asked, and is
 * not counted: it has not answered anything.
 */

import {
  decisionSummary, getConsolidationResidualDistribution, getConsolidationRunDigest,
  listAlertGroups, listD2Evidence, listFeeds, listLatestD2Evidence, listPriceLadders, readCalendar, registryListParameters,
  runDigest, storedMarkdownCandidates, writeBackSummary,
} from '@/api/generated';
import type {
  CycleRunSummary, ConsolidationRunSummary, CursorPageMoveRunSummary, DecisionSummary, FeedStatus, GateD2EvidenceRow,
  AlertGroupPage, InSeasonRunDigest, LadderVersionView, StoredCandidateQueue, WriteBackSummary,
} from '@/api/generated';
import { useApiCollection, useApiQuery } from '@/api';
import { DIGEST_TOP } from '@/features/inseason/cycle-findings';
import { useMoveRuns } from '@/features/inseason/compacting/MoveRunPicker';
import { coverageKind, MARKET_TIMEZONE_KEY, todayIn } from '@/features/foundations/process-calendar/calendar-items';
import { combineReadStates, readState, type QueryLike, type ReadState } from '@/planning';

import {
  candidateFigures, cockpitMoveRun, d2Markets, decisionCounts, exitFigures, moveRunFigures,
  priceFigures, replenishmentFigures,
  type CandidateFigures, type CoverageEntry, type D2Market, type DecisionCounts, type ExitFigures,
  type LadderExpiryFigures, type MoveRunFigures, type PricingCoverageFigures,
  type PriceFigures, type ReplenishmentFigures,
} from './cockpit';
import { priceRunIdOf, type D2Perimeter, type PriceRun } from './scope';
import { resolvePriceRun } from './price-run';

/** A directory read, and the way to ask it again when it failed. */
type Envelope<T> = QueryLike<{ readonly items: readonly T[] }> & { readonly refetch?: () => unknown };

export interface CockpitReadsInput {
  readonly runId: string | null;
  /** The market of the run on screen — the feeds' perimeter. `null`: no run, no feeds read. */
  readonly runCountry: string | null;
  readonly exitRunId: string | null;
  readonly directory: Envelope<CycleRunSummary>;
  readonly exitRuns: Envelope<ConsolidationRunSummary>;
  readonly laneRuns: Envelope<ConsolidationRunSummary>;
  /** Which gate-D2 read the Compacting tile and the transfers row make — see `resolveD2Perimeter`. */
  readonly d2: D2Perimeter;
}

/** One read a tile renders: its query (for `AsyncRegion`'s designed states) and its figures. */
export interface TileRead<T, F> {
  readonly query: QueryLike<T>;
  readonly state: ReadState<unknown>;
  readonly figures: F;
}

export interface CockpitReads {
  /**
   * The run the markdown sections read — the cycle run when it has a price step, else the newest
   * run that does (`resolvePriceRun`). `null` with no cycle run.
   */
  readonly priceRun: PriceRun | null;
  readonly exit: { readonly state: ReadState<unknown>; readonly figures: ExitFigures | null };
  readonly prices: { readonly state: ReadState<unknown>; readonly figures: PriceFigures | null };
  readonly candidates: { readonly state: ReadState<unknown>; readonly figures: CandidateFigures | null };
  readonly openAlerts: number | null;
  readonly writeBack: number | null;
  /** The discount ladders the platform flags as ending soon (R3.4) — `null` until it answers. */
  readonly ladderExpiry: LadderExpiryFigures | null;
  /** The run's market's process-calendar pricing coverage warning (ADR 9068) — `null` for none. */
  readonly pricingCoverage: PricingCoverageFigures | null;
  readonly replenishment: TileRead<InSeasonRunDigest, ReplenishmentFigures | null>;
  /**
   * The one move run the Compacting tile and the moves row state (`cockpitMoveRun`): the run the decision
   * summary counts, else the newest of the market on screen, from the live move-run directory.
   */
  readonly moves: TileRead<CursorPageMoveRunSummary, MoveRunFigures | null>;
  readonly d2: TileRead<readonly GateD2EvidenceRow[], readonly D2Market[]>;
  /** The run's market's feeds, for «Copertura dati». */
  readonly feeds: { readonly state: ReadState<readonly FeedStatus[]>; readonly rows: readonly FeedStatus[] };
  /** What still waits on a planner in the run — `null` until the read answers. */
  readonly decisions: { readonly state: ReadState<unknown>; readonly counts: DecisionCounts | null };
  /**
   * The price run's summary, for the price-steps row's pending count and stake (ADR 6036). The
   * same entry as {@link decisions} when the price run IS the cycle run (F16's usual case) — a
   * second request only when the price steps live on another run.
   */
  readonly priceDecisions: DecisionCounts | null;
  readonly coverage: readonly CoverageEntry[];
}

/** A read of the price run, and the way to ask it again; a read not yet asked is pending, as a disabled query is. */
type PriceQuery<T> = QueryLike<T> & { readonly refetch?: () => unknown };
const NOT_ASKED: PriceQuery<never> = { isPending: true, isError: false, error: null, data: undefined };

const candidatesEmpty = (queue: StoredCandidateQueue): boolean => queue.ranked.length === 0 && queue.unranked.length === 0;

/** Today in the browser, ISO — the ladder read's `as_of` (see the read). */
function isoToday(): string {
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** The ladders the platform flags, counted, with the first end; `null` for none. */
function ladderExpiryFigures(versions: readonly LadderVersionView[]): LadderExpiryFigures | null {
  const ending = versions.filter((version) => version.expiry?.endsSoon === true).map((version) => version.validTo).sort();
  const first = ending[0];
  return first === undefined ? null : { ladders: ending.length, firstEnd: first };
}

/** The calendar's coverage warning as a decision row's figures; `null` when covered or unread. */
function pricingCoverageFigures(
  view: { readonly year: number; readonly pricingCoverage: { readonly warning?: string | null; readonly coveredTo?: string | null; readonly requiredTo?: string | null } } | undefined,
  country: string | null,
): PricingCoverageFigures | null {
  if (view === undefined || country === null) return null;
  const kind = coverageKind(view.pricingCoverage);
  if (kind === 'covered') return null;
  return { country, year: view.year, kind, coveredTo: view.pricingCoverage.coveredTo ?? '', requiredTo: view.pricingCoverage.requiredTo ?? '' };
}

/** A count only for a read that answered with rows — never a `0` for an empty one (ruling 5). */
function countOf<T>(state: ReadState<T>, count: (data: T) => number): number | undefined {
  return state.kind === 'populated' ? count(state.data) : undefined;
}

export function useCockpitReads({
  runId, runCountry, exitRunId, directory, exitRuns, laneRuns, d2: d2Perimeter,
}: CockpitReadsInput): CockpitReads {
  // Which run holds the price steps (F16) first: the directory's rows say it, newest run first
  // (`resolvePriceRun`), with no read of its own. The markdown reads wait for it.
  const priceRun = resolvePriceRun(runId, {
    ready: directory.data !== undefined,
    failed: directory.isError,
    runs: directory.data?.items ?? [],
  });
  const priceRunId = priceRunIdOf(priceRun);
  const queue = useApiQuery(storedMarkdownCandidates, { query: { run_id: priceRunId ?? '' } }, { enabled: priceRunId !== null });
  // The position run in figures, from the two reads that add it up in the store (audit 3): the
  // residual distribution (the exit estate page's own entry) and the run digest's residual. They
  // replace a walk of the option roll-up — 16 requests of 500 on CO.
  const distribution = useApiQuery(
    getConsolidationResidualDistribution,
    { query: { run_id: exitRunId ?? '' } },
    { enabled: exitRunId !== null },
  );
  const exitDigest = useApiQuery(getConsolidationRunDigest, { path: { run_id: exitRunId ?? '' } }, { enabled: exitRunId !== null });
  // The one move run (bata-reqs ADR 9002 stage 3): the live directory the Compacting pages read, one cached
  // entry — the tile's figure and the moves row's context, never the cycle run's retired transfer book.
  const moveRuns = useMoveRuns();
  // The open alerts as the queue counts them (`totals.alerts`: "every alert in the groups the filter
  // matches, not only this page") — one request of one group, in place of seven pages of 1000.
  const exceptions = useApiQuery(listAlertGroups, { query: { open_only: true, group_by: 'country', limit: 1 } });
  // The write-back log's figures as the store adds them up (ADR 6170, `needs_attention` = rejected,
  // unknown or past its deadline, each command once): one request, in place of the log's whole walk.
  const commands = useApiQuery(writeBackSummary, {});
  const feeds = useApiCollection(listFeeds, { query: { country_key: runCountry ?? '' } }, { enabled: runCountry !== null });
  // The discount ladders (owner rulings 2026-10-17, second set, R3.4): the «Scale di sconto» panel's
  // own read. The platform flags a ladder ending soon on the MARKET's date whatever `as_of` is; the
  // date only says which version reads as in force, so the browser's today is enough here.
  const ladders = useApiCollection(listPriceLadders, { query: { as_of: isoToday() } });
  // The process calendar's pricing coverage (owner, 2026-10-17): the run's market's current year, read
  // on the MARKET's date — its `market.timezone` row first; no zone, no read (the date is unknown).
  const zones = useApiCollection(registryListParameters, { query: { as_of: isoToday(), parameter_key: MARKET_TIMEZONE_KEY } }, { enabled: runCountry !== null });
  const zone = (zones.data ?? []).find((row) => row.parameterKey === MARKET_TIMEZONE_KEY
    && row.scopeDimension === 'country' && row.scopeValue === runCountry)?.textValue ?? null;
  const marketToday = zone === null ? null : todayIn(zone, new Date());
  const calendar = useApiQuery(readCalendar, {
    query: { country_key: runCountry ?? '', year: Number((marketToday ?? '0').slice(0, 4)), as_of: marketToday ?? '' },
  }, { enabled: runCountry !== null && marketToday !== null });

  // ADR 9015 — every use case. Each is the chapter screen's own operation with the chapter
  // screen's own parameters (`AllocationScreen`, `RebalancingScreen`, `LostSalesScreen`,
  // `OptionPicker`), so a planner who follows a tile lands on a warm cache entry, and the two
  // cannot disagree about a count.
  // The proposal book is the run digest's (the findings charts' own entry), not 32 pages of 1000.
  const proposals = useApiQuery(runDigest, { path: { run_id: runId ?? '' }, query: { top: DIGEST_TOP } }, { enabled: runId !== null });
  // Gate D2: the dated read at a chosen `?asOf=` (the rebalancing screen's own), else the
  // directory of each market's newest assessment. Exactly one of the two is enabled.
  const d2AsOf = d2Perimeter.kind === 'chosen' ? d2Perimeter.asOf : null;
  const d2Dated = useApiCollection(listD2Evidence, { query: { as_of: d2AsOf ?? '' } }, { enabled: d2AsOf !== null });
  const d2Latest = useApiCollection(listLatestD2Evidence, { query: {} }, { enabled: d2AsOf === null });
  const d2 = d2AsOf === null ? d2Latest : d2Dated;
  const decisions = useApiQuery(decisionSummary, { path: { run_id: runId ?? '' } }, { enabled: runId !== null });
  const priceOnOtherRun = priceRunId !== null && priceRunId !== runId;
  const priceDecisions = useApiQuery(
    decisionSummary,
    { path: { run_id: priceRunId ?? '' } },
    { enabled: priceOnOtherRun },
  );
  // The price run's digest: the cycle run's own (`proposals`) when it IS the price run, else this entry.
  const priceDigest = useApiQuery(
    runDigest,
    { path: { run_id: priceRunId ?? '' }, query: { top: DIGEST_TOP } },
    { enabled: priceOnOtherRun },
  );

  const directoryState = readState(directory);
  const exitRunsState = readState(exitRuns);
  const laneRunsState = readState(laneRuns);
  const queueState = readState(queue, candidatesEmpty);
  const distributionState = readState(distribution, (data) => data.total_options === 0);
  const exitDigestState = readState(exitDigest, () => false);
  const optionsState = combineReadStates(distributionState, exitDigestState);
  const moveRunsState = readState(moveRuns, (data: CursorPageMoveRunSummary) => data.items.length === 0);
  const exceptionsState = readState(exceptions, (data: AlertGroupPage) => data.totals.alerts === 0);
  const commandsState = readState(commands, (data: WriteBackSummary) => data.commands === 0);
  const feedsState = readState(feeds);
  const laddersState = readState(ladders);
  const proposalsState = readState(proposals, (data: InSeasonRunDigest) => data.proposals.proposals === 0);
  const d2State = readState(d2);
  const decisionsState = readState(
    decisions,
    (data: DecisionSummary) => data.proposals_total === 0 && data.transfers_total === 0,
  );
  // The price book: the cycle run's digest when it is the price run, else the price run's own, with
  // the summary of the same run for what is pending. Empty is a run with no price step.
  // Nothing is shown for the cycle run while the price run is still being resolved (it may move).
  const priceBookQuery: PriceQuery<InSeasonRunDigest> = priceRunId === null ? NOT_ASKED : priceOnOtherRun ? priceDigest : proposals;
  const priceSummaryQuery: PriceQuery<DecisionSummary> = priceRunId === null ? NOT_ASKED : priceOnOtherRun ? priceDecisions : decisions;
  const priceBook = priceBookQuery.data?.price_steps;
  const pricesState = combineReadStates(
    readState(priceBookQuery, (data: InSeasonRunDigest) => data.price_steps.price_steps === 0),
    readState(priceSummaryQuery, () => false),
  );

  const length = (rows: readonly unknown[]): number => rows.length;
  const items = (data: { readonly items: readonly unknown[] }): number => data.items.length;

  const coverage: CoverageEntry[] = [
    { id: 'runs', labelKey: 'overview.coverage.read.runs', state: directoryState, count: countOf(directoryState, items), retry: directory.refetch },
    { id: 'exitRuns', labelKey: 'overview.coverage.read.exitRuns', state: exitRunsState, count: countOf(exitRunsState, items), retry: exitRuns.refetch },
    { id: 'laneRuns', labelKey: 'overview.coverage.read.laneRuns', state: laneRunsState, count: countOf(laneRunsState, items), retry: laneRuns.refetch },
    { id: 'moveRuns', labelKey: 'overview.coverage.read.moveRuns', state: moveRunsState, count: countOf(moveRunsState, items), retry: moveRuns.refetch },
    ...(runId === null ? [] : [
      { id: 'priceActions', labelKey: 'overview.coverage.read.priceActions', state: pricesState, count: countOf(pricesState, () => priceBook?.price_steps ?? 0), retry: priceBookQuery.refetch },
      {
        id: 'candidates', labelKey: 'overview.coverage.read.candidates', state: queueState,
        count: countOf(queueState, (data: StoredCandidateQueue) => data.ranked.length + data.unranked.length),
        retry: queue.refetch,
      },
    ]),
    ...(runId === null ? [] : [
      { id: 'proposals', labelKey: 'overview.coverage.read.proposals', state: proposalsState, count: countOf(proposalsState, (data: InSeasonRunDigest) => data.proposals.proposals), retry: proposals.refetch },
      { id: 'decisionSummary', labelKey: 'overview.coverage.read.decisionSummary', state: decisionsState, retry: decisions.refetch },
    ]),
    { id: 'd2', labelKey: 'overview.coverage.read.d2', state: d2State, count: countOf(d2State, length), retry: d2.refetch },
    ...(exitRunId === null ? [] : [
      { id: 'options', labelKey: 'overview.coverage.read.options', state: optionsState, count: countOf(distributionState, (data) => data.total_options), retry: distribution.refetch },
    ]),
    ...(runCountry === null ? [] : [
      { id: 'feeds', labelKey: 'overview.coverage.read.feeds', state: feedsState, count: countOf(feedsState, length), retry: feeds.refetch },
    ]),
    { id: 'exceptions', labelKey: 'overview.coverage.read.exceptions', state: exceptionsState, count: countOf(exceptionsState, (data) => data.totals.alerts), retry: exceptions.refetch },
    { id: 'writeBack', labelKey: 'overview.coverage.read.writeBack', state: commandsState, count: countOf(commandsState, (data: WriteBackSummary) => data.commands), retry: commands.refetch },
    { id: 'ladders', labelKey: 'overview.coverage.read.ladders', state: laddersState, count: countOf(laddersState, length), retry: ladders.refetch },
  ];

  return {
    priceRun,
    exit: { state: optionsState, figures: exitFigures(distribution.data, exitDigest.data) },
    prices: { state: pricesState, figures: priceFigures(priceBook, priceSummaryQuery.data) },
    candidates: { state: queueState, figures: candidateFigures(queue.data) },
    openAlerts: exceptionsState.kind === 'populated' ? exceptionsState.data.totals.alerts : null,
    writeBack: commandsState.kind === 'populated' ? commandsState.data.needs_attention : null,
    ladderExpiry: laddersState.kind === 'populated' ? ladderExpiryFigures(laddersState.data) : null,
    pricingCoverage: pricingCoverageFigures(calendar.data, runCountry),
    replenishment: { query: proposals, state: proposalsState, figures: replenishmentFigures(proposals.data?.proposals) },
    moves: {
      query: moveRuns,
      state: moveRunsState,
      figures: moveRunFigures(cockpitMoveRun(moveRuns.data?.items ?? [], decisionsState.kind === 'unavailable' ? null : decisions.data?.transfers_pending_move_run_id ?? null, runCountry)),
    },
    d2: { query: d2, state: d2State, figures: d2Markets(d2.data ?? []) },
    feeds: { state: feedsState, rows: feeds.data ?? [] },
    decisions: { state: decisionsState, counts: decisionsState.kind === 'unavailable' ? null : decisionCounts(decisions.data) },
    priceDecisions: priceOnOtherRun
      ? (priceDecisions.isError ? null : decisionCounts(priceDecisions.data))
      : decisionsState.kind === 'unavailable' ? null : decisionCounts(decisions.data),
    coverage,
  };
}
